import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy, SimpleChange } from '@angular/core'
import { GoogleChartComponent, ChartSelectionChangedEvent } from 'angular-google-charts'
import { Observable, Subscription } from 'rxjs'
import { lineChartStyle5Lines } from 'src/app/utils/line-chart-style'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/reducers'
import {
  getLoading,
  getReactions,
  getReactionsCsv,
} from 'src/app/selectors/reactions-graph.selector'
import { map, take } from 'rxjs/operators'
import { isEventDrawerOpened } from 'src/app/selectors/shell.selector'
import { getSelectedEventDate } from 'src/app/selectors/event.selector'
import { range } from 'src/app/utils/range'
import * as moment from 'moment'
import { openEventDrawer } from 'src/app/actions/shell.actions'
import { selectEventDate } from 'src/app/actions/event.actions'
import {
  getDataCollectByReactionsGraph,
  getDataCollectLoading,
} from 'src/app/selectors/data-collections.selector'
import { Reactions } from 'src/app/models/reactions.model'
import { getSelectedArtist } from 'src/app/selectors/artist.selector'
import { Artist } from 'src/app/models/artist.model'
import { MediaObserver, MediaChange } from '@angular/flex-layout'
import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout'
import { of } from 'rxjs'

@Component({
    selector: 'app-reactions-graph',
    templateUrl: './reactions-graph.component.html',
    styleUrls: ['./reactions-graph.component.scss'],
    standalone: false
})
export class ReactionsGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('chart')
  chart: GoogleChartComponent

  @ViewChild('container')
  container: ElementRef

  private widthOpened = false
  private widthWhenOpened = 0
  private widthWhenClosed = 0

  sourceName$: Observable<string[]>
  units$: Observable<any[]>
  isLoading$: Observable<boolean>

  dataCollections$: Observable<boolean>
  dataCollectLoading$: Observable<boolean>

  unitsOptions = lineChartStyle5Lines

  csvId = 'reaction'
  csvData$: Observable<any[]>
  csvFileName = 'ネット反応数'
  selectedArtist$: Observable<Artist>

  private subscriptions: Subscription[] = []

  isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(map(it => it.matches))

  checkedSubAccounts: any[]
  checkLabels: { name: string; completed: boolean; disabled: boolean }[] = []
  allSourceName$: Observable<string[]>
  colorsOption: any[]

  constructor(
    private store: Store<AppState>,
    public media: MediaObserver,
    private breakpointObserver: BreakpointObserver
  ) {
    this.subscriptions.push(media.asObservable().subscribe(() => {}))
  }

  ngOnInit() {
    this.isLoading$ = this.store.select(getLoading)
    this.getIniData()

    this.dataCollections$ = this.store.select(getDataCollectByReactionsGraph)
    this.dataCollectLoading$ = this.store.select(getDataCollectLoading)
    // this.sourceName$ = this.store
    //   .select(getReactions)
    //   .pipe(map(it => ['日付'].concat([...new Set(it.map(item => item.sourceName))])))
    // this.units$ = this.store.select(getReactions).pipe(
    //   map(it => {
    //     return this.convert2ChartData(it)
    //   })
    // )
    this.csvData$ = this.store.select(getReactionsCsv)
    this.selectedArtist$ = this.store.select(getSelectedArtist)

    this.subscriptions.push(
      this.store.select(isEventDrawerOpened).subscribe(opened => {
        if (!this.chart) {
          return
        }
        this.widthOpened = opened
        this.chart.width = opened ? this.widthWhenOpened : this.widthWhenClosed
        this.redrawChart()
      })
    )

    this.subscriptions.push(
      this.store.select(getSelectedEventDate).subscribe(date => {
        if (!this.chart) {
          return
        }

        const googleChart = this.chart.chart

        if (!googleChart) {
          return
        }

        if (!date) {
          googleChart.setSelection([])
          return
        }

        const ymd = moment(date).format('YYYY-MM-DD')
        const columnNum = (this.chart.columns || []).length - 1

        this.units$.pipe(take(1)).subscribe(units => {
          const index = units.findIndex(data => data[0] === ymd)

          if (index === -1) {
            return
          }

          const selections: { row: number; column: number }[] = range(
            0,
            columnNum
          ).map(i => ({
            row: index,
            column: i + 1,
          }))

          googleChart.setSelection(selections)
        })
      })
    )

    if (this.media.isActive('lt-md')) {
      this.unitsOptions.legend.position = 'none'
      this.unitsOptions.chartArea.left = 45
    } else {
      this.unitsOptions.legend.position = 'top'
      this.unitsOptions.chartArea.left = 80
    }
  }

  getIniData() {
    this.allSourceName$ = this.store
      .select(getReactions)
      .pipe(map(it => [...new Set(it.map(item => item.sourceName))]))

    this.sourceName$ = this.store
      .select(getReactions)
      .pipe(map(it => ['日付'].concat([...new Set(it.map(item => item.sourceName))])))

    this.units$ = this.store.select(getReactions).pipe(
      map(it => {
        return this.convert2ChartData(it)
      })
    )

    this.subscriptions.push(
      this.allSourceName$.subscribe(sourceNames => {
        this.checkLabels = sourceNames.map(sourceName => ({
          name: sourceName,
          completed: true,
          disabled: false,
        }))
      })
    )

    // 取得先と色をマッピングして、グラフのオプションを設定する
    this.subscriptions.push(
      this.sourceName$
        .pipe(
          map(sourceNames => this.mappingSourceAndColor(sourceNames))
        )
        .subscribe(colors => {
          this.colorsOption = [...colors]

          this.unitsOptions = {
            ...lineChartStyle5Lines,
            colors: this.colorsOption,
          }
        })
    )
  }

  // 選択したsubAccountsのstreamsを表示する
  onChange() {
    // 一つのcheckboxだけをチェックしたら、disableする
    this.disableCheckbox()

    // checked subAccountsを取得する
    this.checkedSubAccounts = []
    this.checkLabels.forEach(it => {
      if (it.completed) {
        this.checkedSubAccounts.push(it.name)
      }
    })

    // 選択したsubAccountsを再取得する
    this.sourceName$ = of(['日付'].concat(this.checkedSubAccounts))

    // 選択したsubAccountsのstreamsを再取得する
    this.units$ = this.store.select(getReactions).pipe(
      map(it => {
        return this.convertSelected2ChartData(it)
      })
    )

    // 選択したsubAccountsの色を再設定する
    this.sourceName$
      .pipe(
        map(it => {
          // streamsの取得先と色をマッピング
          return this.mappingSourceAndColor(it)
        })
      )
      .subscribe(val => {
        // 色のオプションを取得する
        this.colorsOption = []
        val.forEach(d => this.colorsOption.push(d))

        // グラフのオプションを設定する
        this.unitsOptions = {
          ...lineChartStyle5Lines,
          colors: this.colorsOption,
        }
      })
  }

  private disableCheckbox() {
    var count = 0
    for (var i = 0; i < this.checkLabels.length; i++) {
      if (this.checkLabels[i].completed) {
        count++

        if (this.checkLabels[i].disabled) {
          this.checkLabels[i].disabled = false
        }
      }
    }
    if (count === 1) {
      for (var i = 0; i < this.checkLabels.length; i++) {
        if (this.checkLabels[i].completed) {
          this.checkLabels[i].disabled = true
        }
      }
    }
  }

  ngAfterViewInit() {
    if (!this.container) {
      return
    }
    this.widthWhenClosed = this.container.nativeElement.offsetWidth - 20
    this.widthWhenOpened = this.widthWhenClosed - 300
  }

  ngOnDestroy() {
    this.subscriptions.forEach(it => it.unsubscribe())
  }

  onResize() {
    if (!this.chart) {
      return
    }
    // リサイズ時、出来事の開閉状態によって算出が異なる
    if (this.widthOpened) {
      this.widthWhenOpened = this.container.nativeElement.offsetWidth - 20
      this.widthWhenClosed = this.widthWhenOpened + 300
      this.chart.width = this.widthWhenOpened
    } else {
      this.widthWhenClosed = this.container.nativeElement.offsetWidth - 20
      this.widthWhenOpened = this.widthWhenClosed - 300
      this.chart.width = this.widthWhenClosed
    }
    this.redrawChart()
  }

  onSelect(event: ChartSelectionChangedEvent): void {
    const selected = event.selection && event.selection[0]

    if (!selected || selected.row == null) {
      return
    }

    const selectedRow = selected.row

    this.units$.pipe(take(1)).subscribe(units => {
      if (!units[selectedRow]) {
        return
      }

      this.isHandset$.pipe(take(1)).subscribe(isHandset => {
        if (isHandset) {
          return
        }

        this.store.dispatch(openEventDrawer())
        this.store.dispatch(
          selectEventDate({
            eventDate: units[selectedRow][0],
          })
        )
      })
    })
  }

  private convert2ChartData(reactions: Reactions[]): any[] {
    const dates = [...new Set(reactions.map(item => item.transactionDate))]
    const column = []
    dates.forEach(d => {
      const data: any[] = [d]
      column.push(data)
      reactions.map(units => {
        if (d === units.transactionDate) {
          data.push(units.units)
        }
      })
    })
    return column
  }
  private convertSelected2ChartData(streams: Reactions[]): any[] {
    const dates = [...new Set(streams.map(item => item.transactionDate))]
    const column = []
    dates.forEach(d => {
      const data: any[] = [d]
      column.push(data)
      streams.map(stream => {
        if (d === stream.transactionDate) {
          this.checkedSubAccounts.forEach(item => {
            if (item === stream.sourceName) {
              data.push(stream.units)
            }
          })
        }
      })
    })
    return column
  }

  // 取得先と色をマッピング処理
  private mappingSourceAndColor(subAccounts: string[]): string[] {
    const colors = []
    subAccounts.forEach(s => {
      switch (s) {
        case 'Google検索数': {
          colors.push('#039BE5')
          break
        }
        case 'Twitter投稿数': {
          colors.push('#EF5350')
          break
        }
        case 'Shazam数': {
          colors.push('#9CCC65')
          break
        }
        case '日付': {
          break
        }
        default: {
          colors.push('#FFC107')
          break
        }
      }
    })
    return colors
  }

  private redrawChart(): void {
    if (!this.chart) {
      return
    }

    this.chart.ngOnChanges({
      width: new SimpleChange(
        undefined,
        this.chart.width,
        false
      ),
    })
  }
}
